import { z } from "zod";
import { useCacheSlice } from "../hooks";
import type { ApiClient } from "../types";

const entryFilterOptionSchema = z.object({
	value: z.string(),
	label: z.string(),
	sublabel: z.string().optional(),
	code: z.string().nullable().optional(),
	description: z.string().optional(),
	client: z.string().optional(),
});

const entryFilterOptionsSchema = z.object({
	object: z.literal("entry_filters"),
	status: z.array(z.string()),
	application: z.array(z.string()),
	applicationOptions: z.array(entryFilterOptionSchema).optional(),
	matter: z.array(entryFilterOptionSchema),
	client: z.array(z.string()),
	billableCategory: z.array(z.string()),
	task: z.array(z.string()),
	activity: z.array(z.string()),
	taskCode: z.array(entryFilterOptionSchema),
	activityCode: z.array(entryFilterOptionSchema),
	timekeeper: z.array(entryFilterOptionSchema),
});

const entryGroupFilterOptionSchema = z.object({
	value: z.union([z.string(), z.number(), z.boolean()]),
	label: z.string(),
	count: z.number().optional(),
	sublabel: z.string().optional(),
	description: z.string().nullable().optional(),
	group: z.string().nullable().optional(),
	client: z.string().nullable().optional(),
	billableCategory: z.string().nullable().optional(),
	billingCategory: z.string().nullable().optional(),
	status: z.string().nullable().optional(),
	email: z.string().nullable().optional(),
	role: z.string().nullable().optional(),
	department: z.string().nullable().optional(),
	practiceType: z.string().nullable().optional(),
	profType: z.string().nullable().optional(),
	processName: z.string().nullable().optional(),
	code: z.string().nullable().optional(),
});

const entryGroupFilterOptionsSchema = z.object({
	object: z.literal("entry_group_filters"),
	statuses: z.array(entryGroupFilterOptionSchema),
	applications: z.array(entryGroupFilterOptionSchema),
	matters: z.array(entryGroupFilterOptionSchema),
	clients: z.array(entryGroupFilterOptionSchema),
	matterBillableCategories: z.array(entryGroupFilterOptionSchema),
	taskCodes: z.array(entryGroupFilterOptionSchema),
	activityCodes: z.array(entryGroupFilterOptionSchema),
	timekeepers: z.array(entryGroupFilterOptionSchema),
});

export type EntryFilterOption = z.infer<typeof entryFilterOptionSchema>;
export type EntryFilterOptions = z.infer<typeof entryFilterOptionsSchema>;

export const EMPTY_ENTRY_FILTERS: EntryFilterOptions = {
	object: "entry_filters",
	status: [],
	application: [],
	matter: [],
	client: [],
	billableCategory: [],
	task: [],
	activity: [],
	taskCode: [],
	activityCode: [],
	timekeeper: [],
};

function optionValue(
	option: z.infer<typeof entryGroupFilterOptionSchema>,
): string {
	return String(option.value);
}

function optionSublabel(
	option: z.infer<typeof entryGroupFilterOptionSchema>,
): string | undefined {
	return (
		option.sublabel ??
		option.description ??
		option.group ??
		option.email ??
		undefined
	);
}

function mapEntryGroupFilters(data: unknown): EntryFilterOptions {
	const parsed = entryGroupFilterOptionsSchema.parse(data);
	return {
		object: "entry_filters",
		status: parsed.statuses.map(optionValue),
		application: parsed.applications.map((option) => option.label),
		applicationOptions: parsed.applications.map((option) => ({
			value: optionValue(option),
			label: option.label,
			sublabel: optionSublabel(option),
		})),
		matter: parsed.matters.map((option) => ({
			value: optionValue(option),
			label: option.label,
			sublabel: optionSublabel(option),
			description: option.description ?? undefined,
			client: option.client ?? undefined,
		})),
		client: parsed.clients.map(optionValue),
		billableCategory: parsed.matterBillableCategories.map(optionValue),
		task: parsed.taskCodes.map((option) => option.code ?? optionValue(option)),
		activity: parsed.activityCodes.map(
			(option) => option.code ?? optionValue(option),
		),
		taskCode: parsed.taskCodes.map((option) => ({
			value: optionValue(option),
			label: option.label,
			sublabel: optionSublabel(option),
			code: option.code,
		})),
		activityCode: parsed.activityCodes.map((option) => ({
			value: optionValue(option),
			label: option.label,
			sublabel: optionSublabel(option),
			code: option.code,
		})),
		timekeeper: parsed.timekeepers.map((option) => ({
			value: optionValue(option),
			label: option.label,
			sublabel: optionSublabel(option),
		})),
	};
}

export async function fetchEntryFilters(
	api: ApiClient,
): Promise<EntryFilterOptions> {
	const res = await api.makeApiRequest({
		endpoint: "/v2/entry-groups/filters",
		method: "GET",
	});
	if (res.status === "ERROR")
		throw new Error(res.error || "Failed to fetch entry filters");
	return mapEntryGroupFilters(res.data);
}

export function getEntryFilterTimekeeperIds(
	filters: EntryFilterOptions | null | undefined,
): string[] {
	if (!filters) return [];
	const seen = new Set<string>();
	const ids: string[] = [];
	for (const option of filters.timekeeper) {
		const id = option.value.trim();
		if (!id || seen.has(id)) continue;
		seen.add(id);
		ids.push(id);
	}
	return ids;
}

export function useEntryFiltersCache() {
	const { data, isLoading, lastFetchedAt } = useCacheSlice("entry_filters");
	const entryFilters = (data ?? EMPTY_ENTRY_FILTERS) as EntryFilterOptions;
	const isInitialLoading = isLoading && lastFetchedAt === 0;
	return { entryFilters, isLoading: isInitialLoading, lastFetchedAt };
}
